// @ts-nocheck
// Ported verbatim from the standalone NORLIA-PriceCalculator (vera-pricing-
// calculator) so the pricing/cost-engine logic stays byte-for-byte identical.
// Inline styles and untyped props are kept as-is; ts-nocheck is a conscious
// debt for this one big view — Vite (esbuild) builds it regardless and the
// surrounding OTTER glue (App, Sidebar, permissions) is fully typed.
import { useState, useMemo, useEffect } from 'react';
import rates from '../data/ai-rates.json';

// ============================================================================
// NORLIA.AI — VERA Pricing Calculator · v5 (3-mode rebuild)
// Palette: BLACK 80% · BONE 18% · SIGNAL 2%
// ============================================================================

// Mapped onto OTTER's theme tokens (styles/themes.css) so the calculator
// follows the active theme (dark/light · lima/cobalto) instead of the fixed
// NORLIA black/bone/signal palette. Every inline style references C.*, so the
// remap re-skins the whole view without touching the JSX below.
const C = {
  black: 'var(--bg)',
  card: 'var(--panel)',
  cardHi: 'var(--hover)',
  bone: 'var(--ink)',
  signal: 'var(--accent)',
  faint: 'var(--line)',
  soft: 'var(--line2)',
  mute: 'var(--muted)',
  dim: 'var(--ink2)',
  sigSoft: 'var(--accent-bg)',
  sigBorder: 'var(--accent-edge)',
  ok: 'var(--green)',
  warn: 'var(--av-c)',
};
// OTTER typography: Geist for everything, Geist Mono for labels/numbers,
// matching the rest of the app (styles/base.css).
const F = {
  display: "'Geist', 'Manrope', ui-sans-serif, sans-serif",
  heading: "'Geist', 'Manrope', ui-sans-serif, sans-serif",
  body: "'Geist', 'Manrope', ui-sans-serif, sans-serif",
  mono: "'Geist Mono', ui-monospace, monospace",
  grotesk: "'Geist', 'Manrope', ui-sans-serif, sans-serif",
};

const eur = (n) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(
    Number.isFinite(n) ? n : 0
  );
const eur0 = (n) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? n : 0
  );
const int = (n) => new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n || 0);
const eur3 = (n) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 3 }).format(
    Number.isFinite(n) ? n : 0
  );

// Community-maintained price list (same live source OWL uses). Prices come in
// USD per token; multiply by 1e6 to get the $/M the calibrate fields expect.
const LITELLM_URL =
  'https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json';
const perM = (v) => (typeof v === 'number' ? Math.round(v * 1e6 * 1e4) / 1e4 : null);

// ---- primitives ------------------------------------------------------------
const Lbl = ({ children }) => (
  <span
    style={{
      fontFamily: F.mono,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: C.mute,
    }}
  >
    {children}
  </span>
);

const Num = ({ label, value, onChange, step = 1, suffix, hint, min = 0 }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <Lbl>{label}</Lbl>
    <div style={{ position: 'relative' }}>
      <input
        type="number"
        value={value}
        step={step}
        min={min}
        onChange={(e) => onChange(Math.max(min, parseFloat(e.target.value) || 0))}
        onFocus={(e) => (e.target.style.borderColor = C.signal)}
        onBlur={(e) => (e.target.style.borderColor = C.faint)}
        style={{
          width: '100%',
          padding: '10px 12px',
          paddingRight: suffix ? 46 : 12,
          fontFamily: F.mono,
          fontSize: 15,
          background: C.black,
          color: C.bone,
          border: `1px solid ${C.faint}`,
          outline: 'none',
          borderRadius: 0,
          boxSizing: 'border-box',
        }}
      />
      {suffix && (
        <span
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            fontFamily: F.mono,
            fontSize: 11,
            color: C.mute,
            pointerEvents: 'none',
          }}
        >
          {suffix}
        </span>
      )}
    </div>
    {hint && (
      <span style={{ fontFamily: F.body, fontSize: 11, color: C.mute, fontStyle: 'italic' }}>{hint}</span>
    )}
  </label>
);

const Card = ({ title, children, hi = false }) => (
  <div
    style={{
      background: hi ? C.sigSoft : C.card,
      border: `1px solid ${hi ? C.sigBorder : C.faint}`,
      padding: 22,
    }}
  >
    {title && (
      <div style={{ marginBottom: 18, paddingBottom: 10, borderBottom: `1px solid ${C.faint}` }}>
        <h3
          style={{
            fontFamily: F.heading,
            fontWeight: 900,
            fontSize: 15,
            color: C.bone,
            margin: 0,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </h3>
      </div>
    )}
    {children}
  </div>
);

const Row = ({ label, value, sub, hi, strong }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: '9px 0',
      borderBottom: `1px solid ${C.faint}`,
    }}
  >
    <span style={{ fontFamily: F.body, fontSize: 13, color: hi ? C.signal : C.dim }}>
      {label}
      {sub && <span style={{ color: C.mute, fontSize: 11, marginLeft: 8, fontStyle: 'italic' }}>{sub}</span>}
    </span>
    <span
      style={{
        fontFamily: F.mono,
        fontSize: strong ? 16 : 13,
        fontWeight: strong ? 700 : 400,
        color: hi ? C.signal : C.bone,
      }}
    >
      {value}
    </span>
  </div>
);

const Big = ({ label, value, sub, accent }) => (
  <div>
    <Lbl>{label}</Lbl>
    <div
      style={{
        fontFamily: F.grotesk,
        fontWeight: 700,
        fontSize: 40,
        lineHeight: 1,
        color: accent ? C.signal : C.bone,
        margin: '8px 0 4px',
        letterSpacing: '-0.01em',
      }}
    >
      {value}
    </div>
    {sub && <span style={{ fontFamily: F.body, fontSize: 12, color: C.mute }}>{sub}</span>}
  </div>
);

const ModeTab = ({ active, onClick, n, label, desc }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      textAlign: 'left',
      padding: '16px 18px',
      background: active ? C.sigSoft : C.card,
      border: `1px solid ${active ? C.sigBorder : C.faint}`,
      cursor: 'pointer',
      transition: 'all .15s',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
      <span style={{ fontFamily: F.grotesk, fontWeight: 700, fontSize: 18, color: active ? C.signal : C.mute }}>{n}</span>
      <span
        style={{
          fontFamily: F.heading,
          fontWeight: 900,
          fontSize: 15,
          color: active ? C.bone : C.dim,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </span>
    </div>
    <span style={{ fontFamily: F.body, fontSize: 11.5, color: C.mute }}>{desc}</span>
  </button>
);

// ============================================================================
// PROPOSAL MODE — client form + printable 3-page proposal (2 if no ROI)
// ============================================================================
const SECTORS = ['Concesionario', 'Peluquería', 'Barbería', 'Clínica', 'Otro'];

const TXT = {
  ES: {
    formTitle: 'Datos del cliente',
    name: 'Nombre del cliente',
    contact: 'Contacto (nombre, rol)',
    sector: 'Sector',
    lang: 'Idioma de la propuesta',
    validity: 'Validez (días)',
    note: 'El precio sale del modo Precio. No se edita aquí: una sola fuente de verdad.',
    print: 'Imprimir / Guardar como PDF',
    previewTitle: 'Vista previa de la propuesta',
    confidential: 'PROPUESTA COMERCIAL · CONFIDENCIAL',
    forLbl: 'Para',
    heroSub: 'Tu equipo comercial\nque no descansa.',
    s01: 'Tu situación actual',
    s02: 'Tres números que importan',
    s03: 'Qué te entregamos',
    s04: 'Inversión',
    s05: 'El caso económico',
    s06: 'Proyección 3 años',
    s07: 'Próximos pasos',
    solTitle: 'La solución VERA',
    numTitle: 'El número',
    recTitle: 'Lo que recuperas',
    concept: 'CONCEPTO',
    base: 'BASE',
    disc: 'DESCUENTO',
    total: 'TOTAL',
    impl: 'Implementación (pago único)',
    monthly: 'Cuota mensual',
    setupTitle: 'Puesta en marcha (pago único)',
    monthlyTitle: 'Suscripción mensual',
    disclaimerIva: 'Precios sin IVA. Se aplicará el 21 % de IVA vigente. Suscripción mensual sin permanencia.',
    costNow: 'COSTE ACTUAL / AÑO',
    leadsMo: 'LEADS / MES',
    coverage: 'COBERTURA FUERA DE HORARIO',
    saving: 'AHORRO / AÑO 1',
    roiY1: 'ROI AÑO 1',
    payback: 'PAYBACK',
    months: 'meses',
    projCols: ['', 'AÑO 1', 'AÑO 2', 'AÑO 3', 'TOTAL 3A'],
    projRows: ['Coste actual', 'Coste VERA', 'Ahorro neto', 'Ingreso adicional', 'Beneficio total'],
    steps: [
      ['01', 'Firma de contrato y setup', 'Revisión y firma. Pago del setup. Arrancamos la configuración inmediatamente.'],
      ['02', 'Configuración (15–30 días)', 'Onboarding con Meta, integración con Google Calendar y Sheets, configuración del agente.'],
      ['03', 'Go-live + seguimiento', 'Activación en producción. Primera semana de seguimiento conjunto incluida.'],
    ],
    validLbl: 'VALIDEZ DE LA OFERTA',
    validTxt: (d) => `Esta propuesta es válida hasta el ${d}. Pasada esa fecha, precios y descuentos pueden variar.`,
    closeTitle: 'Empecemos',
    closeBody:
      'VERA queda operativa en 15–30 días. La primera semana de seguimiento conjunto va incluida. Sin permanencia: si no funciona, lo dejas.',
    diag: {
      Concesionario:
        'Pierdes leads fuera de horario y cada lead sin contestar en los primeros 5 minutos se va a la competencia. VERA responde en segundos, 24/7, cualifica y agenda la visita sola.',
      Peluquería:
        'Tus profesionales tienen las manos ocupadas y los mensajes se acumulan. Cada cita perdida es una silla vacía. VERA contesta y agenda sin que nadie suelte las tijeras.',
      Barbería:
        'Los clientes escriben cuando pueden — de noche, los domingos — y si no contestas, reservan en otro sitio. VERA agenda 24/7 y te libera para el cliente que tienes delante.',
      Clínica:
        'Cada llamada sin atender es un paciente que no agenda. VERA responde al instante, cualifica el motivo y reserva en tu calendario real, 24/7.',
      Otro:
        'Cada consulta sin respuesta rápida es negocio que se enfría. VERA atiende, cualifica y agenda 24/7 sobre WhatsApp, sin infraestructura nueva.',
    },
    deliv: [
      'Agente VERA en producción 24/7 sobre WhatsApp Business API oficial',
      'Cualificación natural de leads en la conversación, sin formularios ni menús',
      'Reservas automáticas con disponibilidad real de tu Google Calendar',
      'Recordatorios automáticos 24h y 2h antes de cada cita',
      'Pipeline en tiempo real en Google Sheets con histórico completo',
      'Escalado a humano cuando el cliente lo pide o la situación lo requiere',
    ],
    delivConce: 'Brief pre-visita generado por IA 30 min antes de cada cita',
  },
  EN: {
    formTitle: 'Client details',
    name: 'Client name',
    contact: 'Contact (name, role)',
    sector: 'Sector',
    lang: 'Proposal language',
    validity: 'Validity (days)',
    note: 'Price comes from the Price mode. Not edited here: a single source of truth.',
    print: 'Print / Save as PDF',
    previewTitle: 'Proposal preview',
    confidential: 'SALES PROPOSAL · CONFIDENTIAL',
    forLbl: 'For',
    heroSub: 'Your sales team\nthat never sleeps.',
    s01: 'Your current situation',
    s02: 'Three numbers that matter',
    s03: 'What we deliver',
    s04: 'Investment',
    s05: 'The economic case',
    s06: '3-year projection',
    s07: 'Next steps',
    solTitle: 'The VERA solution',
    numTitle: 'The number',
    recTitle: 'What you recover',
    concept: 'ITEM',
    base: 'BASE',
    disc: 'DISCOUNT',
    total: 'TOTAL',
    impl: 'Implementation (one-time)',
    monthly: 'Monthly fee',
    setupTitle: 'Setup (one-time)',
    monthlyTitle: 'Monthly subscription',
    disclaimerIva: 'Prices exclude VAT. The applicable 21% VAT will be added. Monthly subscription, no lock-in.',
    costNow: 'CURRENT COST / YEAR',
    leadsMo: 'LEADS / MONTH',
    coverage: 'AFTER-HOURS COVERAGE',
    saving: 'SAVINGS / YEAR 1',
    roiY1: 'ROI YEAR 1',
    payback: 'PAYBACK',
    months: 'months',
    projCols: ['', 'YEAR 1', 'YEAR 2', 'YEAR 3', '3Y TOTAL'],
    projRows: ['Current cost', 'VERA cost', 'Net savings', 'Added revenue', 'Total benefit'],
    steps: [
      ['01', 'Contract & setup', 'Review and signature. Setup payment. We start configuring immediately.'],
      ['02', 'Configuration (15–30 days)', 'Meta onboarding, Google Calendar and Sheets integration, agent configuration.'],
      ['03', 'Go-live + follow-up', 'Production launch. First week of joint follow-up included.'],
    ],
    validLbl: 'OFFER VALIDITY',
    validTxt: (d) => `This proposal is valid until ${d}. After that date, prices and discounts may change.`,
    closeTitle: "Let's start",
    closeBody:
      'VERA goes live in 15–30 days. The first week of joint follow-up is included. No lock-in: if it does not work, you walk.',
    diag: {
      Concesionario:
        'You lose leads after hours, and any lead not answered within 5 minutes goes to the competition. VERA replies in seconds, 24/7, qualifies and books the visit on its own.',
      Peluquería:
        'Your staff has their hands full and messages pile up. Every missed booking is an empty chair. VERA answers and books without anyone dropping the scissors.',
      Barbería:
        'Customers message when they can — at night, on Sundays — and if you do not answer, they book elsewhere. VERA books 24/7 and frees you for the client in front of you.',
      Clínica:
        'Every unanswered call is a patient who does not book. VERA replies instantly, qualifies the reason and books on your real calendar, 24/7.',
      Otro:
        'Every slow reply is business going cold. VERA answers, qualifies and books 24/7 over WhatsApp, with no new infrastructure.',
    },
    deliv: [
      'VERA agent in 24/7 production over the official WhatsApp Business API',
      'Natural lead qualification in the conversation, no forms or menus',
      'Automatic booking with real availability from your Google Calendar',
      'Automatic reminders 24h and 2h before each appointment',
      'Real-time pipeline in Google Sheets with full history',
      'Human handoff when the customer asks or the situation requires it',
    ],
    delivConce: 'AI-generated pre-visit brief 30 min before each appointment',
  },
};

function ProposalMode(props) {
  const {
    C, F, eur, eur0, clName, setClName, clContact, setClContact, clSector, setClSector,
    propLang, setPropLang, validityDays, setValidityDays, pricePath, cost, price,
    annual, roiCurrentCost, roiLeads, roiUplift, roiRevPerVisit, Num, Card, Lbl,
    onOpenInvoice, onToast, canInvoice,
  } = props;
  const t = TXT[propLang];
  const hasRoi = pricePath === 'roi';
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [genErr, setGenErr] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const [clientId, setClientId] = useState('');
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/admin/clients', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setClientsList(Array.isArray(data.items) ? data.items : []);
      } catch {
        /* ignore — picker stays empty, manual entry still works */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // derived numbers
  const now = new Date();
  const valid = new Date(now.getTime() + validityDays * 864e5);
  const dfmt = (d) =>
    d.toLocaleDateString(propLang === 'ES' ? 'es-ES' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  const ref =
    'VERA-' +
    now.toISOString().slice(2, 10).replace(/-/g, '') +
    '-' +
    (clName || 'CLIENTE').replace(/[^A-Za-z0-9]/g, '').slice(0, 6).toUpperCase();

  const monthly = price.chosen;
  const setup = annual.setup;
  const annualFee = monthly * 12;
  const y1 = setup + annualFee;

  // ROI page numbers (only meaningful with ROI path)
  const curYr = roiCurrentCost;
  const addRevYr = roiLeads * roiUplift * roiRevPerVisit * 12;
  const savingY1 = curYr - y1;
  const roiPct = y1 > 0 ? (savingY1 / y1) * 100 : 0;
  const monthlySaving = curYr / 12 - monthly;
  const payback = monthlySaving > 0 ? Math.ceil(setup / monthlySaving) : null;
  const proj = [
    [t.projRows[0], curYr, curYr, curYr, curYr * 3],
    [t.projRows[1], y1, annualFee, annualFee, y1 + annualFee * 2],
    [t.projRows[2], curYr - y1, curYr - annualFee, curYr - annualFee, curYr * 3 - (y1 + annualFee * 2)],
    [t.projRows[3], addRevYr, addRevYr, addRevYr, addRevYr * 3],
    [
      t.projRows[4],
      curYr - y1 + addRevYr,
      curYr - annualFee + addRevYr,
      curYr - annualFee + addRevYr,
      curYr * 3 - (y1 + annualFee * 2) + addRevYr * 3,
    ],
  ];

  const deliverables = [...t.deliv];
  if (clSector === 'Concesionario') deliverables.splice(2, 0, t.delivConce);

  const closePreview = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  };

  async function generatePdf() {
    setBusy(true);
    setGenErr(null);
    const payload = {
      lang: propLang,
      ref,
      dateStr: dfmt(now),
      validLine: `${t.validLbl} · ${t.validTxt(dfmt(valid))}`,
      confidential: t.confidential,
      forLbl: t.forLbl,
      heroSub: t.heroSub,
      client: { name: clName || '—', contact: clContact || '—', sector: clSector },
      hasRoi,
      diag: t.diag[clSector] || t.diag.Otro,
      deliverables,
      sections: {
        s01: t.s01, s02: t.s02, s03: t.s03, s04: t.s04, s05: t.s05, s06: t.s06, s07: t.s07,
        solTitle: t.solTitle, numTitle: t.numTitle, recTitle: t.recTitle,
        closeTitle: t.closeTitle, closeBody: t.closeBody,
      },
      invest: {
        headers: [t.concept, t.base, t.disc, t.total],
        setupTitle: t.setupTitle,
        setupRows: [{ c: t.impl, base: eur(setup), disc: '—', total: eur(setup) }],
        monthlyTitle: t.monthlyTitle,
        monthlyLabel: t.monthly,
        monthlyValue: eur(monthly),
        disclaimer: t.disclaimerIva,
      },
      kpisTop: [
        [hasRoi && curYr > 0 ? eur0(curYr) : '[—]', t.costNow],
        [roiLeads > 0 ? String(roiLeads) : '[—]', t.leadsMo],
        ['0%', t.coverage],
      ],
      roi: hasRoi
        ? {
            kpis: [
              [savingY1 > 0 ? eur0(savingY1) : '[—]', t.saving],
              [roiPct > 0 ? `${Math.round(roiPct)} %` : '[—]', t.roiY1],
              [payback ? `${payback} ${t.months}` : '[—]', t.payback],
            ],
            projCols: t.projCols,
            projRows: proj.map((r) => [r[0], eur0(r[1]), eur0(r[2]), eur0(r[3]), eur0(r[4])]),
            steps: t.steps,
          }
        : null,
    };
    try {
      const res = await fetch('/admin/proposals/pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      setGenErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function pickClient(id) {
    setClientId(id);
    const cl = clientsList.find((x) => x.id === id);
    if (cl) {
      setClName(cl.name || '');
      if (cl.email) setClContact(cl.email);
    }
  }

  async function createDraftInvoice() {
    if (!clientId) return;
    setCreating(true);
    try {
      const lines = [
        { position: 0, description: `VERA · ${t.impl}`, quantity: 1, unitPrice: Math.round(setup * 100) / 100 },
        { position: 1, description: `VERA · ${t.monthly}`, quantity: 1, unitPrice: Math.round(monthly * 100) / 100 },
      ];
      const res = await fetch('/admin/invoices-issued', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, concept: `VERA · ${clSector}`, lines }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (onToast) onToast('Factura borrador creada en OTTER', 'ok');
      if (onOpenInvoice && data.invoice && data.invoice.id) onOpenInvoice(data.invoice.id);
    } catch (e) {
      if (onToast) onToast(`No se pudo crear la factura: ${e.message || e}`, 'err');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ---- client form ---- */}
      <Card title={t.formTitle}>
        {clientsList.length > 0 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            <Lbl>Cliente de OTTER</Lbl>
            <select
              value={clientId}
              onChange={(e) => pickClient(e.target.value)}
              style={{
                padding: '10px 12px',
                fontFamily: F.body,
                fontSize: 14,
                background: C.black,
                color: C.bone,
                border: `1px solid ${C.faint}`,
                outline: 'none',
              }}
            >
              <option value="">— Cliente nuevo / prospecto —</option>
              {clientsList.map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Lbl>{t.name}</Lbl>
            <input
              value={clName}
              onChange={(e) => setClName(e.target.value)}
              style={{
                padding: '10px 12px',
                fontFamily: F.body,
                fontSize: 14,
                background: C.black,
                color: C.bone,
                border: `1px solid ${C.faint}`,
                outline: 'none',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Lbl>{t.contact}</Lbl>
            <input
              value={clContact}
              onChange={(e) => setClContact(e.target.value)}
              style={{
                padding: '10px 12px',
                fontFamily: F.body,
                fontSize: 14,
                background: C.black,
                color: C.bone,
                border: `1px solid ${C.faint}`,
                outline: 'none',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Lbl>{t.sector}</Lbl>
            <select
              value={clSector}
              onChange={(e) => setClSector(e.target.value)}
              style={{
                padding: '10px 12px',
                fontFamily: F.body,
                fontSize: 14,
                background: C.black,
                color: C.bone,
                border: `1px solid ${C.faint}`,
                outline: 'none',
              }}
            >
              {SECTORS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Lbl>{t.lang}</Lbl>
              <select
                value={propLang}
                onChange={(e) => setPropLang(e.target.value)}
                style={{
                  padding: '10px 12px',
                  fontFamily: F.body,
                  fontSize: 14,
                  background: C.black,
                  color: C.bone,
                  border: `1px solid ${C.faint}`,
                  outline: 'none',
                }}
              >
                <option value="ES">ES</option>
                <option value="EN">EN</option>
              </select>
            </label>
            <Num label={t.validity} value={validityDays} onChange={setValidityDays} suffix="d" />
          </div>
        </div>
        <div
          style={{
            marginTop: 16,
            padding: '12px 14px',
            background: C.cardHi,
            borderLeft: `3px solid ${C.signal}`,
            fontFamily: F.body,
            fontSize: 12,
            color: C.dim,
          }}
        >
          {t.note} {hasRoi ? '' : '· Cliente sin ROI → propuesta de 2 páginas (sin proyección de ahorro inventada).'}
        </div>
        <button
          onClick={generatePdf}
          disabled={busy}
          style={{
            marginTop: 16,
            padding: '12px 20px',
            background: C.signal,
            color: C.black,
            border: 'none',
            fontFamily: F.heading,
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Generando…' : t.print}
        </button>
        {genErr && (
          <div style={{ marginTop: 12, fontFamily: F.mono, fontSize: 12, color: 'var(--red-text)' }}>
            Error al generar la propuesta: {genErr}
          </div>
        )}
        {canInvoice && clientId && (
          <button
            onClick={createDraftInvoice}
            disabled={creating}
            style={{
              marginTop: 16,
              marginLeft: 12,
              padding: '12px 20px',
              background: 'transparent',
              color: C.bone,
              border: `1px solid ${C.signal}`,
              fontFamily: F.heading,
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              cursor: creating ? 'wait' : 'pointer',
              opacity: creating ? 0.6 : 1,
            }}
          >
            {creating ? 'Creando…' : 'Crear factura borrador'}
          </button>
        )}
      </Card>

      {pdfUrl && (
        <div
          onClick={closePreview}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.card,
              border: `1px solid ${C.faint}`,
              padding: 16,
              width: 'min(940px, 94vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <Lbl>{t.previewTitle}</Lbl>
            </div>
            <iframe
              src={pdfUrl}
              title="Vista previa de la propuesta"
              style={{ width: '100%', height: '74vh', border: 'none', background: '#fff' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
              <a
                href={pdfUrl}
                download={`${ref}.pdf`}
                style={{
                  padding: '10px 18px',
                  background: C.signal,
                  color: C.black,
                  fontFamily: F.heading,
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                }}
              >
                Descargar PDF
              </a>
              <button
                onClick={closePreview}
                style={{
                  padding: '10px 18px',
                  background: 'transparent',
                  color: C.bone,
                  border: `1px solid ${C.soft}`,
                  fontFamily: F.heading,
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
export function Calculadora({ onOpenInvoice, onToast, canInvoice }) {
  // ---- COST ENGINE inputs (calibrate quarterly) ---------------------------
  // Defaults live in ../data/ai-rates.json (single source for the Calculadora UI
  // and the pricing-control audit). Values unchanged — only their home moved.
  const [usdEur, setUsdEur] = useState(rates.usdEur);
  const [inRate, setInRate] = useState(rates.inRate); // $/M input
  const [outRate, setOutRate] = useState(rates.outRate); // $/M output
  const [cacheWrite, setCacheWrite] = useState(rates.cacheWrite); // $/M
  const [cacheRead, setCacheRead] = useState(rates.cacheRead); // $/M
  const [waUtility, setWaUtility] = useState(rates.waUtility); // €/utility template (ES) — verify against Meta rate card
  const [waMarketing, setWaMarketing] = useState(rates.waMarketing); // €/marketing msg
  const [groqRate, setGroqRate] = useState(rates.groqRate); // $/h transcribed audio (Whisper)
  const [audioSecs, setAudioSecs] = useState(rates.audioSecs); // avg seconds per voice note
  const [railwayUsd, setRailwayUsd] = useState(rates.railwayUsd); // $/mo per tenant project (service + Postgres + Redis + egress)
  const [sharedUsd, setSharedUsd] = useState(rates.sharedUsd); // $/mo shared infra (panel + Supabase + OWL)
  const [tenants, setTenants] = useState(rates.tenants); // active tenants sharing that infra
  const [supportRate, setSupportRate] = useState(rates.supportRate); // €/h
  const [supportHrs, setSupportHrs] = useState(rates.supportHrs); // h/client/mo

  // token shape — calibrated from the VERA N2 benchmark (2026-06-20, no prompt
  // caching): ~7500 in / ~300 out tokens per turn, ~6.5 turns per conversation,
  // so ~$0.027/turn ≈ ~$0.16-0.18 (IA) per conversation. NB: 7500/300 costs the
  // same per turn as the old 7000/400 — this is the real token split, not a price
  // change.
  const [msgsPerTurn, setMsgsPerTurn] = useState(1.3); // debounce groups bursts: msgs per AI turn
  const [inTokens, setInTokens] = useState(7500); // full prompt + history per turn (measured)
  const [outTokens, setOutTokens] = useState(300); // measured ~290-300
  const [turnsPerConv, setTurnsPerConv] = useState(6.5); // measured avg turns per conversation
  const [cacheable, setCacheable] = useState(0); // 0 until prompt caching ships in VERA
  const [cacheHit, setCacheHit] = useState(0.8); // share of turns reading cache vs writing it

  // ---- CLIENT inputs (per quote) ------------------------------------------
  const [msgsPerMonth, setMsgsPerMonth] = useState(9000); // total in + out
  const [audiosPerMonth, setAudiosPerMonth] = useState(450); // voice notes received
  const [citasPerMonth, setCitasPerMonth] = useState(300);
  const [remindersPerCita, setRemindersPerCita] = useState(2); // utility templates per booking
  const [marketingMsgs, setMarketingMsgs] = useState(0);

  // ---- PRICE inputs -------------------------------------------------------
  const [pricePath, setPricePath] = useState('floor'); // 'floor' | 'roi'
  const [margin, setMargin] = useState(4);
  const [setupFee, setSetupFee] = useState(600);

  // ROI path
  const [roiCurrentCost, setRoiCurrentCost] = useState(0); // €/yr client spends today
  const [roiLeads, setRoiLeads] = useState(0);
  const [roiUplift, setRoiUplift] = useState(0.3);
  const [roiRevPerVisit, setRoiRevPerVisit] = useState(0);
  const [roiValueShare, setRoiValueShare] = useState(0.2); // you charge X% of value created

  // ---- PROPOSAL (client identity) -----------------------------------------
  const [clName, setClName] = useState('');
  const [clContact, setClContact] = useState('');
  const [clSector, setClSector] = useState('Concesionario');
  const [propLang, setPropLang] = useState('ES');
  const [validityDays, setValidityDays] = useState(30);

  const [mode, setMode] = useState('cost');

  // ---- LITELLM RATE SYNC ----------------------------------------------------
  const [syncModel, setSyncModel] = useState('claude-sonnet-4-6');
  const [sync, setSync] = useState({ status: 'idle', at: null, error: null });

  async function syncRates() {
    setSync({ status: 'loading', at: null, error: null });
    try {
      const res = await fetch(LITELLM_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const key = syncModel.trim();
      const entry = data[key] || data[`anthropic/${key}`];
      if (!entry) throw new Error(`Modelo «${key}» no encontrado en LiteLLM`);
      const inP = perM(entry.input_cost_per_token);
      const outP = perM(entry.output_cost_per_token);
      if (inP == null || outP == null) throw new Error(`«${key}» no trae precios por token`);
      setInRate(inP);
      setOutRate(outP);
      const cw = perM(entry.cache_creation_input_token_cost);
      const cr = perM(entry.cache_read_input_token_cost);
      if (cw != null) setCacheWrite(cw);
      if (cr != null) setCacheRead(cr);
      setSync({ status: 'ok', at: new Date(), error: null });
    } catch (err) {
      setSync({ status: 'error', at: null, error: err.message });
    }
  }

  // ---- COST MODEL ---------------------------------------------------------
  // Chain: messages → AI turns (debounce) → tokens → €. The message is what
  // you can estimate for a client; the turn is what actually costs money.
  const cost = useMemo(() => {
    const turns = msgsPerTurn > 0 ? msgsPerMonth / msgsPerTurn : 0;

    // Every turn sends the full prompt. The cacheable block is read from
    // cache on cacheHit of turns and written on the rest; with cacheable = 0
    // (no prompt caching in VERA yet) everything bills as plain input.
    const cacheTok = Math.min(cacheable, inTokens);
    const claudeUSD =
      (turns * cacheHit * cacheTok * cacheRead) / 1e6 +
      (turns * (1 - cacheHit) * cacheTok * cacheWrite) / 1e6 +
      (turns * (inTokens - cacheTok) * inRate) / 1e6 +
      (turns * outTokens * outRate) / 1e6;
    const claude = claudeUSD * usdEur;

    const groq = ((audiosPerMonth * audioSecs) / 3600) * groqRate * usdEur;

    // Meta: client-initiated conversations are free. You pay utility
    // templates (reminders outside the 24h window) plus marketing pushes.
    const wa = citasPerMonth * remindersPerCita * waUtility + marketingMsgs * waMarketing;

    const infraTenant = railwayUsd * usdEur;
    const infraShared = (sharedUsd / Math.max(1, tenants)) * usdEur;

    const support = supportRate * supportHrs;

    const total = claude + groq + wa + infraTenant + infraShared + support;
    return {
      turns,
      claude,
      groq,
      wa,
      infraTenant,
      infraShared,
      support,
      total,
      perMsg: msgsPerMonth > 0 ? total / msgsPerMonth : 0,
      perCita: citasPerMonth > 0 ? total / citasPerMonth : 0,
      // Per-conversation lens (ties to the VERA benchmark's $/conversation): the
      // VARIABLE IA cost of one conversation = €/turn × measured turns/conv.
      // Excludes monthly fixed infra/support — that's the true marginal floor.
      claudePerTurn: turns > 0 ? claude / turns : 0,
      iaPerConversation: turns > 0 ? (claude / turns) * turnsPerConv : 0,
    };
  }, [
    msgsPerMonth,
    msgsPerTurn,
    inTokens,
    outTokens,
    turnsPerConv,
    cacheable,
    cacheHit,
    cacheWrite,
    cacheRead,
    inRate,
    outRate,
    usdEur,
    audiosPerMonth,
    audioSecs,
    groqRate,
    citasPerMonth,
    remindersPerCita,
    waUtility,
    marketingMsgs,
    waMarketing,
    railwayUsd,
    sharedUsd,
    tenants,
    supportRate,
    supportHrs,
  ]);

  // ---- PRICE MODEL --------------------------------------------------------
  const price = useMemo(() => {
    const floorPrice = cost.total * margin; // cost-plus
    const grossFloor = floorPrice - cost.total;

    // ROI path
    const extraConv = roiLeads * roiUplift;
    const extraRevYr = extraConv * roiRevPerVisit * 12;
    const valueYr = roiCurrentCost + extraRevYr; // total annual value to client
    const valuePrice = (valueYr * roiValueShare) / 12; // monthly, capturing share
    const grossValue = valuePrice - cost.total;

    const chosen = pricePath === 'roi' ? valuePrice : floorPrice;
    const gross = chosen - cost.total;
    const marginPct = chosen > 0 ? (gross / chosen) * 100 : 0;
    const effMultiple = cost.total > 0 ? chosen / cost.total : 0;

    return {
      floorPrice,
      grossFloor,
      valuePrice,
      grossValue,
      valueYr,
      extraRevYr,
      chosen,
      gross,
      marginPct,
      effMultiple,
      belowFloor: chosen < cost.total,
    };
  }, [cost.total, margin, pricePath, roiLeads, roiUplift, roiRevPerVisit, roiCurrentCost, roiValueShare]);

  const annual = useMemo(() => {
    const m = price.chosen;
    return { setup: setupFee, monthly: m, year1: setupFee + m * 12, recurring: m * 12 };
  }, [price.chosen, setupFee]);

  // ---- shared layout ------------------------------------------------------
  const wrap = { maxWidth: 1080, margin: '0 auto' };
  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };

  return (
    // `body` gives us OTTER's scroll container (flex:1 + overflow:auto) and the
    // themed background, like every other view; no fixed 100vh that overflows.
    <div
      className="body"
      style={{
        color: C.bone,
        fontFamily: F.body,
        paddingBottom: 48,
      }}
    >
      <div style={wrap}>
        {/* ---- HEADER ---- */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <span
              style={{
                fontFamily: F.display,
                fontSize: 26,
                color: C.bone,
                letterSpacing: '-0.01em',
              }}
            >
              NORLIA
            </span>
            <span style={{ display: 'inline-block', width: 9, height: 9, background: C.signal, margin: '0 2px 3px 1px' }} />
            <span style={{ fontFamily: F.display, fontSize: 26, color: C.bone }}>AI</span>
          </div>
          <span style={{ fontFamily: F.mono, fontSize: 10, color: C.mute, letterSpacing: '0.14em' }}>
            VERA · PRICING · V5 · {new Date().getFullYear()}
          </span>
        </div>
        <h1
          style={{
            fontFamily: F.heading,
            fontWeight: 900,
            fontSize: 32,
            color: C.bone,
            margin: '4px 0 6px',
            textTransform: 'uppercase',
            letterSpacing: '-0.01em',
          }}
        >
          ¿Cuánto me cuesta? ¿Cuánto le cobro?
        </h1>
        <p style={{ fontFamily: F.body, fontSize: 13, color: C.mute, margin: '0 0 24px', maxWidth: 680 }}>
          Dos preguntas, en este orden. Primero tu coste real (objetivo). Luego el precio
          (decisión comercial, distinta según el cliente tenga ROI medible o no).
        </p>

        {/* ---- MODE TABS ---- */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
          <ModeTab
            active={mode === 'cost'}
            onClick={() => setMode('cost')}
            n="01"
            label="Coste"
            desc="Qué me cuesta este cliente a mí"
          />
          <ModeTab
            active={mode === 'price'}
            onClick={() => setMode('price')}
            n="02"
            label="Precio"
            desc="Qué le cobro al cliente"
          />
          <ModeTab
            active={mode === 'calib'}
            onClick={() => setMode('calib')}
            n="03"
            label="Calibrar"
            desc="Tarifas · revisión trimestral"
          />
          <ModeTab
            active={mode === 'prop'}
            onClick={() => setMode('prop')}
            n="04"
            label="Propuesta"
            desc="PDF para cerrar · usa el precio de arriba"
          />
        </div>

        {/* ================= MODE 01 · COST ================= */}
        {mode === 'cost' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={grid2}>
              <Card title="Este cliente">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <Num
                    label="Mensajes / mes"
                    value={msgsPerMonth}
                    onChange={setMsgsPerMonth}
                    step={100}
                    hint="Entrantes + salientes, recordatorios incluidos. OWL te da el real."
                  />
                  <Num
                    label="Notas de voz / mes"
                    value={audiosPerMonth}
                    onChange={setAudiosPerMonth}
                    step={50}
                    hint="Audios que VERA transcribe (Groq Whisper)"
                  />
                  <Num label="Citas / mes" value={citasPerMonth} onChange={setCitasPerMonth} step={10} />
                  <Num
                    label="Recordatorios / cita"
                    value={remindersPerCita}
                    onChange={setRemindersPerCita}
                    hint="Plantillas utility fuera de ventana 24h (24h y 2h antes = 2)"
                  />
                  <Num
                    label="Mensajes marketing / mes"
                    value={marketingMsgs}
                    onChange={setMarketingMsgs}
                    hint="0 si el cliente no hace campañas push"
                  />
                  <Num
                    label="Horas soporte / mes"
                    value={supportHrs}
                    onChange={setSupportHrs}
                    step={0.25}
                    suffix="h"
                    hint={`Este cliente. Tarifa ${eur(supportRate)}/h (se edita en Calibrar)`}
                  />
                </div>
              </Card>

              <Card title="Lo que me cuesta / mes" hi>
                <Big
                  label="Coste total / mes"
                  value={eur(cost.total)}
                  sub={`${eur3(cost.perMsg)} por mensaje · ${eur(cost.perCita)} por cita`}
                  accent
                />
                <div style={{ marginTop: 18 }}>
                  <Row label="Claude (tokens)" value={eur(cost.claude)} sub={`${int(cost.turns)} turnos de IA`} />
                  <Row label="Groq Whisper (audios)" value={eur(cost.groq)} />
                  <Row label="WhatsApp (Meta)" value={eur(cost.wa)} sub="plantillas utility + marketing" />
                  <Row label="Railway (proyecto tenant)" value={eur(cost.infraTenant)} />
                  <Row
                    label="Infra compartida"
                    value={eur(cost.infraShared)}
                    sub={`panel + OWL ÷ ${Math.max(1, tenants)} tenant${tenants > 1 ? 's' : ''}`}
                  />
                  <Row label="Soporte imputado" value={eur(cost.support)} sub={`${supportHrs} h × ${eur(supportRate)}`} />
                  <Row label="TOTAL COSTE" value={eur(cost.total)} strong hi />
                </div>
                <div style={{ marginTop: 14 }}>
                  <Row label="Coste por mensaje" value={eur3(cost.perMsg)} />
                  <Row label="Coste por cita gestionada" value={eur(cost.perCita)} />
                  <Row
                    label={`Coste IA por conversación (${turnsPerConv} turnos)`}
                    value={eur3(cost.iaPerConversation)}
                  />
                </div>
              </Card>
            </div>

            <div
              style={{
                background: C.cardHi,
                border: `1px solid ${C.faint}`,
                borderLeft: `3px solid ${C.signal}`,
                padding: '16px 20px',
              }}
            >
              <Lbl>Recordatorio · revisión trimestral</Lbl>
              <p style={{ fontFamily: F.body, fontSize: 12.5, color: C.dim, margin: '8px 0 0', lineHeight: 1.5 }}>
                Este coste depende de las tarifas de Anthropic y Meta, que cambian. Revisa el
                modo <strong>Calibrar</strong> cada 3 meses. Cláusula sugerida para el contrato del cliente:{' '}
                <span style={{ color: C.bone, fontStyle: 'italic' }}>
                  «Los precios se revisan trimestralmente. Si las tarifas de los proveedores de
                  infraestructura (Anthropic, Meta) se salen del rango previsto, NORLIA podrá ajustar
                  la cuota, comunicándolo con 30 días de antelación.»
                </span>
              </p>
            </div>
          </div>
        )}

        {/* ================= MODE 02 · PRICE ================= */}
        {mode === 'price' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* path selector */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setPricePath('floor')}
                style={{
                  flex: 1,
                  padding: '14px 16px',
                  textAlign: 'left',
                  background: pricePath === 'floor' ? C.sigSoft : C.card,
                  border: `1px solid ${pricePath === 'floor' ? C.sigBorder : C.faint}`,
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontFamily: F.heading, fontWeight: 900, fontSize: 14, color: C.bone, textTransform: 'uppercase' }}>
                  Sin ROI · suelo + margen
                </div>
                <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.mute, marginTop: 3 }}>
                  Barbería, peluquería, gimnasio… no se ahorra un call center
                </div>
              </button>
              <button
                onClick={() => setPricePath('roi')}
                style={{
                  flex: 1,
                  padding: '14px 16px',
                  textAlign: 'left',
                  background: pricePath === 'roi' ? C.sigSoft : C.card,
                  border: `1px solid ${pricePath === 'roi' ? C.sigBorder : C.faint}`,
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontFamily: F.heading, fontWeight: 900, fontSize: 14, color: C.bone, textTransform: 'uppercase' }}>
                  Con ROI · valor capturado
                </div>
                <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.mute, marginTop: 3 }}>
                  Concesionario, clínica, inmobiliaria… ahorro medible
                </div>
              </button>
            </div>

            <div style={grid2}>
              <Card title={pricePath === 'roi' ? 'Valor para el cliente' : 'Tu margen'}>
                {pricePath === 'floor' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Num
                      label="Multiplicador sobre coste"
                      value={margin}
                      onChange={setMargin}
                      step={0.5}
                      suffix="×"
                      hint="Tu coste × este número. ×4 = margen del 75%, ×5 = 80%."
                    />
                    <Num
                      label="Setup inicial (pago único)"
                      value={setupFee}
                      onChange={setSetupFee}
                      suffix="€"
                      hint="Regla práctica: 2-3 × la cuota mensual (cubre la implantación)"
                    />
                    <div
                      style={{
                        background: C.black,
                        border: `1px solid ${C.faint}`,
                        padding: 14,
                        fontFamily: F.body,
                        fontSize: 11.5,
                        color: C.mute,
                        lineHeight: 1.5,
                      }}
                    >
                      Sin ROI no hay ahorro que enseñar. El precio sale de tu suelo de coste por
                      un margen, y se contrasta con lo que paga el sector por comodidad. El coste
                      es el <strong style={{ color: C.dim }}>suelo</strong>: nunca por debajo.
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Num
                      label="Coste actual del cliente / año"
                      value={roiCurrentCost}
                      onChange={setRoiCurrentCost}
                      suffix="€"
                      hint="Lo que gasta hoy en call center / gestión que VERA sustituye"
                    />
                    <Num label="Leads / mes" value={roiLeads} onChange={setRoiLeads} />
                    <Num
                      label="Mejora de conversión con VERA"
                      value={roiUplift}
                      onChange={setRoiUplift}
                      step={0.05}
                      hint="0.3 = +30% de conversión sobre leads"
                    />
                    <Num
                      label="Ingreso por visita / cierre"
                      value={roiRevPerVisit}
                      onChange={setRoiRevPerVisit}
                      suffix="€"
                    />
                    <Num
                      label="% del valor que capturas"
                      value={roiValueShare}
                      onChange={setRoiValueShare}
                      step={0.05}
                      hint="0.2 = cobras el 20% del valor anual que generas"
                    />
                    <Num label="Setup inicial (pago único)" value={setupFee} onChange={setSetupFee} suffix="€" />
                  </div>
                )}
              </Card>

              <Card title="Qué cobrar" hi>
                <Big
                  label={pricePath === 'roi' ? 'Precio sugerido / mes (por valor)' : 'Precio sugerido / mes'}
                  value={eur(price.chosen)}
                  sub={`Coste ${eur(cost.total)} · margen ${price.marginPct.toFixed(0)}% · ×${price.effMultiple.toFixed(
                    1
                  )}`}
                  accent
                />
                <div style={{ marginTop: 18 }}>
                  <Row label="Coste (suelo)" value={eur(cost.total)} />
                  <Row label="Precio cliente / mes" value={eur(price.chosen)} hi strong />
                  <Row label="Beneficio / mes" value={eur(price.gross)} />
                  <Row label="Setup (una vez)" value={eur(annual.setup)} />
                  <Row label="Año 1 (setup + 12 meses)" value={eur0(annual.year1)} strong />
                </div>
                <div style={{ marginTop: 14 }}>
                  <Row
                    label="Precio por mensaje"
                    value={eur3(msgsPerMonth > 0 ? price.chosen / msgsPerMonth : 0)}
                    sub="sanity check vs. mercado"
                  />
                  <Row
                    label="Precio por cita gestionada"
                    value={eur(citasPerMonth > 0 ? price.chosen / citasPerMonth : 0)}
                  />
                </div>
                {pricePath === 'roi' && (
                  <div style={{ marginTop: 14 }}>
                    <Row label="Valor anual para el cliente" value={eur0(price.valueYr)} sub="ahorro + ingreso extra" />
                    <Row
                      label="Le cobras / año"
                      value={eur0(price.chosen * 12)}
                      sub={`${(roiValueShare * 100).toFixed(0)}% del valor`}
                    />
                  </div>
                )}
                {price.belowFloor && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 12,
                      border: `1px solid ${C.warn}`,
                      background: 'rgba(227,147,60,0.08)',
                      fontFamily: F.body,
                      fontSize: 12,
                      color: C.warn,
                    }}
                  >
                    ⚠ El precio está por debajo de tu coste. Pierdes dinero con este cliente.
                  </div>
                )}
              </Card>
            </div>

            {pricePath === 'roi' && (
              <div
                style={{
                  background: C.cardHi,
                  border: `1px solid ${C.faint}`,
                  borderLeft: `3px solid ${C.signal}`,
                  padding: '16px 20px',
                  fontFamily: F.body,
                  fontSize: 12.5,
                  color: C.dim,
                  lineHeight: 1.5,
                }}
              >
                <Lbl>Por qué este camino y no “coste × 3”</Lbl>
                <p style={{ margin: '8px 0 0' }}>
                  Tu coste de servir a VERA es muy bajo (céntimos/pocos euros de Claude). Si cobras
                  “coste × 3” a un cliente que se ahorra {eur0(price.valueYr)} al año, dejas casi
                  todo ese valor sobre la mesa. Con ROI medible, el precio sale del{' '}
                  <strong style={{ color: C.bone }}>valor</strong>, no del coste — el coste solo
                  marca el suelo.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ================= MODE 03 · CALIBRATE ================= */}
        {mode === 'calib' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                background: 'rgba(227,147,60,0.07)',
                border: `1px solid ${C.warn}`,
                padding: '14px 18px',
                fontFamily: F.body,
                fontSize: 12.5,
                color: C.warn,
              }}
            >
              ⚠ Tocar solo en la revisión trimestral. Estos valores son las tarifas reales de
              Anthropic y Meta. Si cambian y no los actualizas, todos tus precios quedan mal.
            </div>
            <div style={grid2}>
              <Card title="Claude · Anthropic">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Lbl>Modelo (clave LiteLLM)</Lbl>
                    <input
                      value={syncModel}
                      onChange={(e) => setSyncModel(e.target.value)}
                      style={{
                        padding: '10px 12px',
                        fontFamily: F.mono,
                        fontSize: 13,
                        background: C.black,
                        color: C.bone,
                        border: `1px solid ${C.faint}`,
                        outline: 'none',
                      }}
                    />
                  </label>
                  <button
                    onClick={syncRates}
                    disabled={sync.status === 'loading'}
                    style={{
                      padding: '10px 14px',
                      background: sync.status === 'loading' ? C.cardHi : C.signal,
                      color: sync.status === 'loading' ? C.mute : C.black,
                      border: 'none',
                      fontFamily: F.heading,
                      fontWeight: 700,
                      fontSize: 12,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      cursor: sync.status === 'loading' ? 'wait' : 'pointer',
                    }}
                  >
                    {sync.status === 'loading' ? 'Sincronizando…' : 'Sincronizar tarifas (LiteLLM)'}
                  </button>
                  {sync.status === 'ok' && (
                    <span style={{ fontFamily: F.mono, fontSize: 10, color: C.ok }}>
                      ✓ Tarifas de LiteLLM ·{' '}
                      {sync.at.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })} · fuente
                      comunitaria, verifica si vas a cerrar precio
                    </span>
                  )}
                  {sync.status === 'error' && (
                    <span style={{ fontFamily: F.mono, fontSize: 10, color: C.warn }}>⚠ {sync.error}</span>
                  )}
                  <Num label="Input $/M tokens" value={inRate} onChange={setInRate} step={0.25} />
                  <Num label="Output $/M tokens" value={outRate} onChange={setOutRate} step={0.25} />
                  <Num label="Cache write $/M" value={cacheWrite} onChange={setCacheWrite} step={0.25} />
                  <Num label="Cache read $/M" value={cacheRead} onChange={setCacheRead} step={0.05} />
                  <Num label="USD → EUR" value={usdEur} onChange={setUsdEur} step={0.01} />
                </div>
              </Card>
              <Card title="WhatsApp · Groq">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Num
                    label="Utility €/plantilla (ES)"
                    value={waUtility}
                    onChange={setWaUtility}
                    step={0.001}
                    hint="Verificar contra la tarifa vigente de Meta para España"
                  />
                  <Num label="Marketing €/msg" value={waMarketing} onChange={setWaMarketing} step={0.01} />
                  <Num label="Groq Whisper $/hora audio" value={groqRate} onChange={setGroqRate} step={0.01} />
                  <Num label="Duración media audio" value={audioSecs} onChange={setAudioSecs} step={5} suffix="s" />
                </div>
              </Card>
              <Card title="Infra · Soporte">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Num
                    label="Railway $/mes por tenant"
                    value={railwayUsd}
                    onChange={setRailwayUsd}
                    hint="Servicio (~1 GB) + Postgres + Redis + egress. Calibrar con factura real."
                  />
                  <Num
                    label="Infra compartida $/mes"
                    value={sharedUsd}
                    onChange={setSharedUsd}
                    hint="Panel (Railway + Supabase) + OWL, total para todos los tenants"
                  />
                  <Num
                    label="Tenants activos"
                    value={tenants}
                    onChange={setTenants}
                    min={1}
                    hint="La infra compartida se prorratea entre ellos"
                  />
                  <Num label="Soporte €/hora" value={supportRate} onChange={setSupportRate} hint="Las horas por cliente se ajustan en el modo Coste" />
                </div>
              </Card>
              <Card title="Forma de los tokens">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Num
                    label="Mensajes por turno de IA"
                    value={msgsPerTurn}
                    onChange={setMsgsPerTurn}
                    step={0.1}
                    hint="El debounce agrupa ráfagas: 9.000 msgs ≈ 7.000 turnos → 1,3"
                  />
                  <Num
                    label="Input tokens / turno"
                    value={inTokens}
                    onChange={setInTokens}
                    step={500}
                    hint="System prompt completo + historial. Medido en VERA: 6-8k"
                  />
                  <Num label="Output tokens / turno" value={outTokens} onChange={setOutTokens} step={50} />
                  <Num
                    label="Turnos por conversación"
                    value={turnsPerConv}
                    onChange={setTurnsPerConv}
                    step={0.5}
                    hint="Medido en VERA: ~6,5 turnos por conversación (para el coste IA por conversación)"
                  />
                  <Num
                    label="Tokens cacheables"
                    value={cacheable}
                    onChange={setCacheable}
                    step={500}
                    hint="0 hasta implementar prompt caching en VERA. Luego: parte estable del prompt."
                  />
                  <Num
                    label="Cache hit (0-1)"
                    value={cacheHit}
                    onChange={setCacheHit}
                    step={0.05}
                    hint="Fracción de turnos que leen la caché en vez de escribirla"
                  />
                </div>
              </Card>
            </div>
            <div
              style={{
                fontFamily: F.mono,
                fontSize: 11,
                color: C.mute,
                background: C.card,
                border: `1px solid ${C.faint}`,
                padding: 16,
                lineHeight: 1.7,
              }}
            >
              <div style={{ color: C.dim, marginBottom: 8 }}>// fórmula del coste, para que la entiendas</div>
              TURNOS = mensajes / msgsPorTurno
              <br />
              CLAUDE = turnos·[hit·cacheable·readRate + (1−hit)·cacheable·writeRate + (input−cacheable)·inRate + output·outRate]
              <br />
              WHISPER = audios·segundos/3600 · $/h
              <br />
              WHATSAPP = citas·recordatorios · €utility + msgsMkt · €marketing
              <br />
              INFRA = railway + compartida/tenants
              <br />
              COSTE = (CLAUDE + WHISPER + INFRA)·(USD→EUR) + WHATSAPP + SOPORTE
              <br />
              PRECIO = COSTE × margen <span style={{ color: C.mute }}>(sin ROI) </span>
              ó VALOR × %captura <span style={{ color: C.mute }}>(con ROI)</span>
            </div>
          </div>
        )}

        {/* ================= MODE 04 · PROPOSAL ================= */}
        {mode === 'prop' && (
          <ProposalMode
            C={C}
            F={F}
            eur={eur}
            eur0={eur0}
            clName={clName}
            setClName={setClName}
            clContact={clContact}
            setClContact={setClContact}
            clSector={clSector}
            setClSector={setClSector}
            propLang={propLang}
            setPropLang={setPropLang}
            validityDays={validityDays}
            setValidityDays={setValidityDays}
            pricePath={pricePath}
            cost={cost}
            price={price}
            annual={annual}
            roiCurrentCost={roiCurrentCost}
            roiLeads={roiLeads}
            roiUplift={roiUplift}
            roiRevPerVisit={roiRevPerVisit}
            Num={Num}
            Card={Card}
            Lbl={Lbl}
            onOpenInvoice={onOpenInvoice}
            onToast={onToast}
            canInvoice={canInvoice}
          />
        )}

        {/* ---- FOOTER ---- */}
        <div
          style={{
            marginTop: 40,
            paddingTop: 16,
            borderTop: `1px solid ${C.soft}`,
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: F.mono,
            fontSize: 10,
            color: C.mute,
            letterSpacing: '0.1em',
          }}
        >
          <span>NORLIA.AI · VERA PRICING · V5</span>
          <span>SONNET · WHATSAPP CLOUD API · {new Date().getFullYear()}</span>
        </div>
      </div>
    </div>
  );
}
